import { useState, useEffect } from "react";
import { API_ROOT } from "../utils/apiConfig";

/**
 * Custom hook to fetch published blogs from the backend
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search query
 * @param {string} options.category - Filter by category
 * @param {boolean} options.hero - Fetch featured/hero articles
 * @returns {Object} - { data, isLoading, error }
 */
export const usePublishedBlogs = (options = {}) => {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Build query params
        const params = new URLSearchParams();
        if (options.page) params.append("page", options.page);
        if (options.limit) params.append("limit", options.limit);
        if (options.search) params.append("search", options.search);
        if (options.category) params.append("category", options.category);
        if (options.hero) params.append("hero", true);

        const url = `${API_ROOT}/api/blogs/published?${params.toString()}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch blogs: ${response.statusText}`);
        }

        const result = await response.json();
        setData(result.data || {});
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setError(err.message);
        // Return empty structure to prevent UI breaks
        setData({ items: [], meta: { page: 1, totalPages: 0, totalItems: 0 } });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, [
    options.page,
    options.limit,
    options.search,
    options.category,
    options.hero,
  ]);

  return {
    data,
    isLoading,
    error,
  };
};
